<template>
  <div class="rich-text-editor" :class="{ 'rich-text-editor--focused': focused, 'rich-text-editor--error': tooLong }">
    <div class="d-flex align-center px-1 rich-text-toolbar">
      <v-tooltip v-for="action in actions" :key="action.command" top open-delay="400">
        <template v-slot:activator="{ on, attrs }">
          <!-- mousedown.prevent keeps the selection in the text while clicking -->
          <v-btn
            icon
            small
            tabindex="-1"
            v-bind="attrs"
            v-on="on"
            :color="active[action.command] ? 'primary' : null"
            :class="{ 'rich-text-button--active': active[action.command] }"
            @mousedown.prevent
            @click="exec(action.command)"
          >
            <v-icon small>{{ action.icon }}</v-icon>
          </v-btn>
        </template>
        <span>{{ $t(`components.RichTextEditor.${action.command}`) }}{{ action.shortcut ? ` (⌘/Ctrl + ${action.shortcut})` : '' }}</span>
      </v-tooltip>
    </div>
    <!-- Its content is the browser's, not Vue's: it is only written when value changes from outside -->
    <div
      ref="content"
      contenteditable="true"
      role="textbox"
      aria-multiline="true"
      class="rich-text-content"
      :class="{ 'rich-text-content--empty': empty }"
      :style="{ minHeight: minHeight }"
      :data-placeholder="placeholder"
      @input="onInput"
      @paste="onPaste"
      @drop.prevent
      @keydown="onKeydown"
      @focus="onFocus"
      @blur="focused = false"
    ></div>
    <div v-if="maxLength" class="text-caption text-right px-2 rich-text-counter" :class="tooLong ? 'error--text' : 'grey--text'">{{ value.length }} / {{ maxLength }}</div>
  </div>
</template>
<script>
import { htmlToMarkdown, markdownToHtml } from '@/utils/feedbackMarkdown'

const ACTIONS = [
  { command: 'bold', icon: 'mdi-format-bold', shortcut: 'B' },
  { command: 'italic', icon: 'mdi-format-italic', shortcut: 'I' },
  { command: 'underline', icon: 'mdi-format-underline', shortcut: 'U' },
  { command: 'insertUnorderedList', icon: 'mdi-format-list-bulleted' },
  { command: 'insertOrderedList', icon: 'mdi-format-list-numbered' }
]
const SHORTCUTS = { b: 'bold', i: 'italic', u: 'underline' }

// WYSIWYG editor for the Markdown subset in utils/feedbackMarkdown: v-model is that Markdown
export default {
  props: {
    value: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    // Counted on the Markdown, as the backend checks it
    maxLength: {
      type: Number,
      default: null
    },
    minHeight: {
      type: String,
      default: '72px'
    },
    autofocus: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      actions: ACTIONS,
      active: {},
      focused: false,
      // Last Markdown sent up, to tell the parent's own changes apart from its echo of the user's
      emitted: null,
      empty: !this.value
    }
  },
  computed: {
    tooLong () {
      return !!this.maxLength && this.value.length > this.maxLength
    }
  },
  methods: {
    render () {
      this.emitted = this.value
      this.$refs.content.innerHTML = this.value ? markdownToHtml(this.value) : ''
      this.updateEmpty()
    },
    // An empty list item isn't stored but must still hide the placeholder, or the list couldn't be started
    updateEmpty () {
      const content = this.$refs.content
      this.empty = !content.textContent && !content.querySelector('li')
    },
    onInput () {
      const markdown = htmlToMarkdown(this.$refs.content)
      this.updateEmpty()
      this.emitted = markdown
      this.$emit('input', markdown)
      this.updateActive()
    },
    exec (command) {
      if (!this.focused) {
        this.focus()
      }
      document.execCommand(command, false, null)
      this.onInput()
    },
    // Only plain text is pasted: formatting from elsewhere would bring what the editor can't keep
    onPaste (event) {
      event.preventDefault()
      const text = (event.clipboardData || window.clipboardData).getData('text/plain')
      if (text) {
        document.execCommand('insertText', false, text)
      }
    },
    onKeydown (event) {
      const modifier = event.metaKey || event.ctrlKey
      if (modifier && event.key === 'Enter') {
        event.preventDefault()
        this.$emit('submit')
        return
      }
      const command = modifier && !event.altKey && !event.shiftKey ? SHORTCUTS[event.key.toLowerCase()] : null
      if (command) {
        event.preventDefault()
        this.exec(command)
      }
    },
    onFocus () {
      this.focused = true
      // Tags rather than inline styles, and a div per line, in every browser
      document.execCommand('styleWithCSS', false, false)
      document.execCommand('defaultParagraphSeparator', false, 'div')
      this.updateActive()
    },
    updateActive () {
      const selection = window.getSelection()
      const content = this.$refs.content
      if (!content || !selection || selection.rangeCount === 0 || !content.contains(selection.anchorNode)) {
        return
      }
      let active = {}
      ACTIONS.forEach(action => {
        try {
          active[action.command] = document.queryCommandState(action.command)
        } catch (error) {
          active[action.command] = false
        }
      })
      this.active = active
    },
    // Puts the caret at the end of the text
    focus () {
      const content = this.$refs.content
      if (!content) {
        return
      }
      content.focus()
      const range = document.createRange()
      range.selectNodeContents(content)
      range.collapse(false)
      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(range)
    }
  },
  mounted () {
    this.render()
    document.addEventListener('selectionchange', this.updateActive)
    if (this.autofocus) {
      this.$nextTick(() => this.focus())
    }
  },
  beforeDestroy () {
    document.removeEventListener('selectionchange', this.updateActive)
  },
  watch: {
    value (value) {
      if (value !== this.emitted) {
        this.render()
      }
    }
  }
}
</script>
<style scoped>
.rich-text-editor {
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
  background: #fff;
  transition: border-color 0.15s;
}
.rich-text-editor:hover {
  border-color: rgba(0, 0, 0, 0.87);
}
.rich-text-editor--focused,
.rich-text-editor--focused:hover {
  border-color: var(--v-primary-base);
  box-shadow: inset 0 0 0 1px var(--v-primary-base);
}
.rich-text-editor--error,
.rich-text-editor--error:hover {
  border-color: var(--v-error-base);
}
.rich-text-toolbar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  min-height: 32px;
}
.rich-text-button--active {
  background: rgba(0, 0, 0, 0.08);
}
/* Long messages grow the editor up to a point, then scroll inside it */
.rich-text-content {
  max-height: 240px;
  overflow-y: auto;
  padding: 8px 12px;
  outline: none;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.875rem;
  line-height: 1.375rem;
}
.rich-text-content--empty:before {
  content: attr(data-placeholder);
  color: rgba(0, 0, 0, 0.38);
  pointer-events: none;
}
.rich-text-content ::v-deep ul,
.rich-text-content ::v-deep ol {
  padding-left: 24px;
  margin: 2px 0;
}
.rich-text-counter {
  line-height: 1.25rem;
}
</style>
